<script setup lang="ts">
import { computed, ref } from 'vue';
import { useInjectGameEngine } from '@/composables/useGameEngine';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import BaseDialog from './BaseDialog.vue';
import BaseButton from './BaseButton.vue';
import { HitStatus } from '@/types';

const gameEngine = useInjectGameEngine();
const showHideNotice = ref(false);

// Any note already judged means switching now would retroactively grant the
// S/S+ ceiling to notes played while the target line was still visible.
const hasJudgedNotes = computed(() =>
  gameEngine.currentGameNotes.value.some(
    (note) =>
      note.hitStatus === HitStatus.Missed ||
      note.hitStatus === HitStatus.Good ||
      note.hitStatus === HitStatus.Perfect,
  ),
);

const handleToggle = (visible: boolean) => {
  gameEngine.targetLineVisible.value = visible;

  // Only hiding mid-run needs a heads-up: the run is already latched as
  // "target line was visible" (see targetLineVisibleDuringRun in
  // useGameEngine), so this toggle alone can no longer unlock S/S+ for it.
  if (!visible && hasJudgedNotes.value) {
    showHideNotice.value = true;
  }
};

const dismissHideNotice = () => {
  showHideNotice.value = false;
};

const restartAndHide = async () => {
  showHideNotice.value = false;
  gameEngine.targetLineVisible.value = false;
  gameEngine.initGame();
  await gameEngine.startGame();
};
</script>

<template>
  <div class="target-line-row">
    <div class="target-line-text">
      <span class="target-line-label">Target line</span>
      <span class="target-line-caption">
        Hide it to unlock S and S+ grades — nail the timing blind.
      </span>
    </div>
    <USwitch
      :model-value="gameEngine.targetLineVisible.value"
      aria-label="Show target line"
      :ui="{ base: 'data-[state=checked]:bg-text-primary' }"
      @update:model-value="handleToggle"
    />
  </div>

  <BaseDialog
    v-model:is-visible="showHideNotice"
    @close="dismissHideNotice"
  >
    <template #title>
      <ExclamationTriangleIcon class="w-6 h-6 text-warning" />
      This run won't earn S/S+
    </template>

    <p class="mb-6 text-text-secondary">
      Part of this piece was already played with the target line visible, so this run stays capped
      at A+ even with it hidden from here on. Restart the song to unlock S/S+.
    </p>

    <div class="flex justify-end gap-3">
      <BaseButton
        variant="text"
        @click="dismissHideNotice"
      >
        Keep playing
      </BaseButton>
      <BaseButton
        color="primary"
        @click="restartAndHide"
      >
        Restart &amp; hide
      </BaseButton>
    </div>
  </BaseDialog>
</template>

<style scoped>
.target-line-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 44px;
}

.target-line-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.target-line-label {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.target-line-caption {
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--color-text-tertiary, var(--color-text-secondary));
}
</style>
